import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Pin, Calendar, Globe, MapPin, Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import CategoryBadge from "./CategoryBadge";
import PhotoCarousel from "./PhotoCarousel";
import ReactionSummary from "./ReactionSummary";
import VideoEmbed from "./VideoEmbed";
import { excerpt } from "../lib/markdown";
import { findVideo } from "../lib/media";
import { formatDate, timeAgo } from "../lib/date";
import { useUser } from "../contexts/UserContext";
import { api } from "../lib/api";

const LEVEL_SHORT = {
  ceo: "CEO", direction: "Direction", dn: "DN",
  dr: "DR", consultant: "Consultant", president: "Président",
};

export default function PostCard({ post, category, author, onDeleted, reactionSummary }) {
  const { user } = useUser();
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Dans le fil on affiche un EXTRAIT en texte brut, pas le markdown rendu :
  //   - line-clamp-3 ne sait pas tronquer proprement des listes et des titres
  //   - la carte entière est déjà un <Link> : y imbriquer les liens auto-détectés
  //     produisait un <a> dans un <a> (HTML invalide)
  const preview = useMemo(() => excerpt(post.content, 260), [post.content]);

  // Vidéo citée dans le post — sert d'illustration quand il n'y a pas de photo.
  const video = useMemo(
    () => (post.photos?.length ? null : findVideo(post.content)),
    [post.photos, post.content]
  );

  const canDelete =
    user?.user_key === post.author_id ||
    ["ceo", "direction"].includes(user?.hierarchy_level);

  const doDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleting(true);
    try {
      await api.delete(`/posts/${post.post_id}`);
      toast.success("Post supprimé.");
      onDeleted?.(post.post_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec suppression.");
    } finally {
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      layout
      className="mb-4 relative"
    >
      {canDelete && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setConfirmDel(true);
          }}
          data-testid={`post-delete-${post.post_id}`}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur hover:bg-red-50 transition-colors"
          style={{ border: "1px solid #E5E5E5" }}
          title="Supprimer le post"
        >
          <Trash2 size={14} color="#EF4444" />
        </button>
      )}

      <Link
        to={`/posts/${post.post_id}`}
        data-testid={`post-card-${post.post_id}`}
        className="block bg-white rounded-[12px] overflow-hidden transition-shadow"
        style={{
          border: "1px solid #E5E5E5",
          boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.03)";
        }}
      >
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <CategoryBadge category={category} />
            {post.is_pinned && (
              <div
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{ background: "#F77F00", color: "#FFF" }}
                data-testid={`post-pinned-${post.post_id}`}
              >
                <Pin size={10} /> Épinglé
              </div>
            )}
          </div>

          <h2
            className="text-xl sm:text-2xl font-bold mb-3 leading-tight"
            style={{ color: "#1A1A1A", fontFamily: "'Sora', sans-serif" }}
          >
            {post.title}
          </h2>

          {post.photos && post.photos.length > 0 && (
            <div className="mb-4 -mx-1">
              <PhotoCarousel photos={post.photos} testid={`post-card-photos-${post.post_id}`} />
            </div>
          )}

          {video && (
            <div className="mb-4">
              <VideoEmbed
                video={video}
                compact
                testid={`post-card-video-${post.post_id}`}
              />
            </div>
          )}

          <p className="text-base leading-relaxed mb-5 line-clamp-3" style={{ color: "#1A1A1A" }}>
            {preview}
          </p>

          {post.event_date && (
            <div
              className="flex items-center gap-2 text-sm mb-4 px-3 py-2 rounded-lg flex-wrap"
              style={{ background: "#FAFAFA", color: "#666" }}
            >
              <Calendar size={14} color="#F77F00" />
              <span className="font-medium" style={{ color: "#1A1A1A" }}>
                {formatDate(post.event_date)}
              </span>
              {post.event_location && (
                <>
                  <MapPin size={14} className="ml-2" />
                  <span>{post.event_location}</span>
                </>
              )}
            </div>
          )}

          <div
            className="flex items-center justify-between pt-4 mt-2 border-t flex-wrap gap-3"
            style={{ borderColor: "#E5E5E5" }}
          >
            <div className="flex items-center gap-3 min-w-0">
              {author?.avatar_url ? (
                <img
                  src={author.avatar_url}
                  alt=""
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ background: "#F77F00" }}
                >
                  {(author?.first_name?.[0] || "?") + (author?.last_name?.[0] || "")}
                </div>
              )}
              <div className="min-w-0">
                <div
                  className="text-sm font-semibold truncate"
                  style={{ color: "#1A1A1A" }}
                  data-testid={`post-author-${post.post_id}`}
                >
                  {author?.name || `${author?.first_name || ""} ${author?.last_name || ""}`.trim() || "Membre APOLLO"}
                </div>
                <div className="text-xs flex items-center gap-1.5" style={{ color: "#666" }}>
                  {author?.hierarchy_level && (
                    <>
                      <span>{LEVEL_SHORT[author.hierarchy_level]}</span>
                      <span>·</span>
                    </>
                  )}
                  <span>{timeAgo(post.published_at || post.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs" style={{ color: "#666" }}>
              <ReactionSummary
                // reactionSummary vient du fil (appel groupé). Tant qu'il n'est
                // pas arrivé, on retombe sur le total déjà stocké sur le post :
                // le compteur ne disparaît jamais au chargement.
                summary={
                  reactionSummary || {
                    counts: { like: post.reactions_count || 0 },
                    total: post.reactions_count || 0,
                  }
                }
                commentsCount={post.comments_count || 0}
                testid={`post-reactions-count-${post.post_id}`}
              />
              {post.target_scope === "global" && (
                <Globe size={12} title="Visible monde entier" />
              )}
            </div>
          </div>
        </div>
      </Link>

      {confirmDel && (
        <div
          className="absolute inset-0 bg-white/95 backdrop-blur rounded-[12px] flex items-center justify-center z-20 p-4"
          onClick={(e) => e.preventDefault()}
          data-testid={`post-delete-modal-${post.post_id}`}
        >
          <div className="text-center max-w-md">
            <div
              className="w-12 h-12 rounded-full inline-flex items-center justify-center mb-3"
              style={{ background: "#FEE2E2" }}
            >
              <Trash2 size={20} color="#EF4444" />
            </div>
            <h3 className="text-base font-bold mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
              Supprimer ce post ?
            </h3>
            <p className="text-xs mb-4" style={{ color: "#666" }}>
              Action irréversible. Réactions, commentaires, événements
              et photos seront aussi supprimés.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmDel(false);
                }}
                disabled={deleting}
                data-testid={`post-delete-cancel-${post.post_id}`}
                className="px-4 py-2 rounded-full text-sm font-semibold border bg-white"
                style={{ borderColor: "#E5E5E5" }}
              >
                Annuler
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                data-testid={`post-delete-confirm-${post.post_id}`}
                className="px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "#EF4444" }}
              >
                {deleting ? "Suppression…" : "Oui, supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
